import type {
  AvailableStateCode,
  CashTransfer,
  CashTransferDetails
} from '@/components/root/details/tabs/cashTransfers/cashTransfer.model';
import type { ResponsePayloadCashTransferDetails } from '@/components/root/details/transfer/model/responsePayload';
import { getConfig } from '@/config/config';
import type { LegalEntity } from '@/model/LegalEntity';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { Failure, WebApiResponseWithFailures } from './WebApiResponse';
import { webApiResponseBaseQuery } from './webApiResponseBaseQuery';

interface RootIBANSearchQueryParam {
  rootId: string;
  currency?: string;
}

interface RootIBANsPayloadItem {
  root: string;
  portfolioCurreny: string;
  ibans: IBANObject[];
}

export interface IBANObject {
  iban: string;
  currency: string;
  generalAccountNumber: string;
  accountId: string;
  type: IbanItemType;
}
export type IbanItemType = 'CAV' | 'TECHNICAL_ACCOUNT' | 'OTHER'

export interface ValidationResult {
  transferId: string;
  status: AvailableStateCode;
}
export interface CashTransferListAdvancedRequest {
  rootId: string | undefined,
  dateFrom: string | undefined,
  dateTo: string | undefined,
  referenceCode: string | undefined,
  status: AvailableStateCode | undefined,
  legalEntity: LegalEntity,
}

const prefixUrl = getConfig().prefixUrl ?? '';

export const cashTransferApi = createApi({
  reducerPath: 'cashTransferApi',
  keepUnusedDataFor: 3_600, // By defaut keep query data 1h even if unused
  baseQuery: webApiResponseBaseQuery,
  tagTypes: ['CashTransferList'],
  endpoints: (builder) => ({
    getIBANs: builder.query<IBANObject[], RootIBANSearchQueryParam>({
      query: ({ rootId, currency }) => {
        return {
          method: 'GET',
          url: `${prefixUrl}/cashtransfers/ibans/${rootId}${currency ? `?currency=${currency}` : ''}`,
        };
      },
      transformResponse: ({
        data: [{ ibans }],
      }: WebApiResponseWithFailures<RootIBANsPayloadItem>) => ibans as IBANObject[],
    }),
    getCashTransferAdvancedList: builder.query<
      {data: CashTransfer[], failures: Failure[]},
      CashTransferListAdvancedRequest
    >({
      query: ({ rootId, dateFrom, dateTo, referenceCode, status, legalEntity }: CashTransferListAdvancedRequest) => {
        const params = new URLSearchParams();
        if (dateFrom) params.append('fromDate', dateFrom)
        if (dateTo) params.append('toDate', dateTo)
        if (rootId) params.append('rootId', rootId)
        if (referenceCode) params.append('reference', referenceCode)
        if (status) params.append('status', status)
        params.append('legalEntity', legalEntity)
        return {
          method: 'GET',
          url: `${prefixUrl}/cashtransfers/lists/searches?${params.toString()}`,
        };
      },
      transformResponse: ({
        data,
        failures,
      }: WebApiResponseWithFailures<CashTransfer>) => {
        if (failures.length > 0) {
          console.warn("There were some failures while getting list of cashtransfers (adv. search)")
          return {data, failures}
        }
        return {data, failures: []};
      },
      providesTags: [{ type: 'CashTransferList', id: 'LIST1' }],
    }),
    getCashTransferList: builder.query<
      {data: CashTransfer[], failures: Failure[]},
      { rootId: string; fromDate?: string; toDate?: string }
    >({
      query: ({ rootId, fromDate, toDate }) => {
        const params = new URLSearchParams();
        if (fromDate) params.append('fromDate', fromDate);
        if (toDate) params.append('toDate', toDate);
        return {
          method: 'GET',
          url: `${prefixUrl}/cashtransfers/lists/roots/${rootId}?${params.toString()}`,
        };
      },
      transformResponse: ({
        data: cashTransferList,
        failures,
      }: WebApiResponseWithFailures<CashTransfer>) => {
        if (failures.length > 0) {
          console.warn("There were some failures while getting list of cashtransfers")
          return {data: cashTransferList as CashTransfer[], failures}
        }
        return {data: cashTransferList as CashTransfer[], failures: []};
      },
      providesTags: [{ type: 'CashTransferList', id: 'LIST' }],
    }),
    getCashTransferToValidateList: builder.query<CashTransfer[], LegalEntity>({
      query: (entity:LegalEntity) => ({
        method: 'GET',
        url: `${prefixUrl}/cashtransfers/lists/validates`,
        params: {
          legalEntity: entity
        },
      }),
      transformResponse: ({
        data: cashTransferList,
        failures,
      }: WebApiResponseWithFailures<CashTransfer>) => {
        if (failures.length > 0) {
          console.warn("There were some failures while getting list of cashtransfers to validate")
        }
        return cashTransferList as CashTransfer[];
      },
      providesTags: [{ type: 'CashTransferList', id: 'LIST' }],
    }),
    getCashTransferById: builder.query<ResponsePayloadCashTransferDetails, { referenceCode: string, entity: LegalEntity, rootId: string }>({
      keepUnusedDataFor: 0, // always get the freshly data
      query: ({referenceCode, entity, rootId }) => ({
        method: 'GET',
        url: `${prefixUrl}/cashtransfers/singles/details/${referenceCode}`,
        params: {
          legalEntity: entity,
          rootId: rootId
        },
      }),
      transformResponse: ({
        data: [transfer],
      }: WebApiResponseWithFailures<ResponsePayloadCashTransferDetails>) => transfer,
    }),
    checkCanCreateCashTransfer: builder.mutation<
      WebApiResponseWithFailures<ResponsePayloadCashTransferDetails>,
      { cashtransfer: CashTransferDetails, entity: LegalEntity }
    >({
      query: ({cashtransfer, entity}) => ({
        method: 'POST',
        url: `${prefixUrl}/cashtransfers/checks/creates`,
        body: {
          ...cashtransfer,
        },
        params: {
          legalEntity: entity
        },
        transformResponse: ({
          data: [details],
          failures = [] as Failure[],
        }: WebApiResponseWithFailures<ResponsePayloadCashTransferDetails>) => ({
          data: details,
          failures,
        }),
      }),
    }),
    checkCanUpdateCashTransfer: builder.mutation<
      WebApiResponseWithFailures<ResponsePayloadCashTransferDetails>,
      { payload: CashTransferDetails; referenceCode: string; entity: LegalEntity }
    >({
      query: ({ payload: cashTransfer, referenceCode, entity }) => ({
        method: 'POST',
        url: `${prefixUrl}/cashtransfers/checks/updates/${referenceCode}`,
        body: {
          ...cashTransfer,
        },
        params: {
          legalEntity: entity
        },
        transformResponse: ({
          data: [details],
          failures = [] as Failure[],
        }: WebApiResponseWithFailures<ResponsePayloadCashTransferDetails>) => ({
          data: details,
          failures,
        }),
      }),
    }),
    createCashTransfer: builder.mutation<WebApiResponseWithFailures<string>, { payload: CashTransferRequest, entity: LegalEntity, rootId: string }>({
      query: ({payload: cashTransfer, entity, rootId}) => ({
        method: 'POST',
        url: `${prefixUrl}/cashtransfers/validators/creates`,
        body: {
          ...cashTransfer,
        },
        params: {
          legalEntity: entity,
          rootId: rootId
        },
      }),
      invalidatesTags: [{ type: 'CashTransferList', id: 'LIST' }],
    }),
    updateCashTransfer: builder.mutation<WebApiResponseWithFailures<string>,{ payload: CashTransferRequest; referenceCode: string, entity: LegalEntity, rootId: string }>({
      query: ({ payload: cashTransfer, referenceCode, entity, rootId }) => ({
        method: 'PUT',
        url: `${prefixUrl}/cashtransfers/validators/updates/${referenceCode}`,
        body: {
          ...cashTransfer,
        },
        params: {
          legalEntity: entity,
          rootId: rootId
        },
      }),
      invalidatesTags: [{ type: 'CashTransferList', id: 'LIST' }],
    }),
    validateCashTransfer: builder.mutation<WebApiResponseWithFailures<ValidationResult>, { referenceCode: string, entity: LegalEntity, rootId: string }>({
      query: (request) => ({
        method: 'POST',
        url: `${prefixUrl}/cashtransfers/validators/validates/${request.referenceCode}`,
        params: {
          legalEntity: request.entity,
          rootId: request.rootId
        },
      }),
      invalidatesTags: [{ type: 'CashTransferList', id: 'LIST' }],
    }),
    cancelCashTransfer: builder.mutation<WebApiResponseWithFailures<string>, { referenceCode: string, entity: LegalEntity, payload: CashTransferCancelRequest, rootId: string }>({
      query: ({payload: comments, referenceCode, entity, rootId}) => ({
        method: 'POST',
        url: `${prefixUrl}/cashtransfers/validators/cancels/${referenceCode}`,
        body: {
          ...comments
        },
        params: {
          legalEntity: entity,
          rootId: rootId
        },
      }),
      invalidatesTags: [{ type: 'CashTransferList', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetIBANsQuery,
  useLazyGetIBANsQuery,
  useGetCashTransferListQuery,
  useLazyGetCashTransferListQuery,
  useGetCashTransferByIdQuery,
  useLazyGetCashTransferByIdQuery,
  useLazyGetCashTransferToValidateListQuery,
  useCreateCashTransferMutation,
  useUpdateCashTransferMutation,
  useValidateCashTransferMutation,
  useCancelCashTransferMutation,
  useCheckCanCreateCashTransferMutation,
  useCheckCanUpdateCashTransferMutation,
  useLazyGetCashTransferAdvancedListQuery,
} = cashTransferApi;

export type CashTransferRequest = {
  cashTransferData: CashTransferDetails;
  draftMode: boolean;
};

export type CashTransferCancelRequest = {
  comment : string | null;
}
